<template>
  <div class="product-aside-cart-container">
    <div class="product-aside-cart-item">
      <form class="product-aside-personalise-form-inline" @submit.prevent>
        <div v-if="isB2B">
          <span>Recipient name: {{ b2bCart.recipientName }}</span>
        </div>

        <div v-if="isB2B && b2bMultiPersonalisation">
          <div v-if="feature.B2B.autofill_personalisation" class="form-input">
            <div @click="toggleB2BDropdown" :class="dropdownCss()">
              <ul data-test="product-variant-dropdown">
                <li class="selected">Select option</li>
                <li class="variant-dropdown" @click="setB2BDropdown('name')">
                  {{ b2bCart.recipientName }}
                </li>
                <li v-if="b2bCompany.companyName" class="variant-dropdown" @click="setB2BDropdown('company')">
                  {{ b2bCompany.companyName }}
                </li>
                <li class="variant-dropdown" @click="setB2BDropdown('custom')">
                  Custom message
                </li>
              </ul>
            </div>
          </div>
          <div v-if="feature.B2B.autofill_personalisation && b2bDropdown.selected !== ''" class="form-input">
            <input
              class="sensitive"
              autofocus
              :disabled="adding"
              ref="input"
              id="personalisation-input"
              placeholder="personalised message"
              :maxlength="max_message_length"
              v-model="message"
              type="text"
            />
            <span :class="charCount > max_message_length ? 'count-over' : 'count'" >{{charCount}}/{{max_message_length}}</span>
          </div>
          <div v-else class="form-input">
            <input
              class="sensitive"
              autofocus
              :disabled="adding"
              ref="input"
              id="personalisation-input"
              placeholder="personalised message"
              :maxlength="max_message_length"
              v-model="message"
              type="text"
            />
            <span :class="charCount > max_message_length ? 'count-over' : 'count'" >{{charCount}}/{{max_message_length}}</span>
          </div>
        </div>

        <div class="form-input" v-if="!isB2B && b2bMultiPersonalisation">
          <input
            class="sensitive"
            autofocus
            :disabled="adding"
            ref="input"
            id="personalisation-input"
            placeholder="personalised message"
            :maxlength="max_message_length"
            v-model="message"
            type="text"
          />
          <span :class="charCount > max_message_length ? 'count-over' : 'count'" >{{charCount}}/{{max_message_length}}</span>
        </div>

        <div class="form-input" v-if="!b2bMultiPersonalisation">
          <input
            class="sensitive"
            autofocus
            :disabled="adding"
            ref="input"
            id="personalisation-input"
            placeholder="personalised message"
            :maxlength="max_message_length"
            v-model="message"
            type="text"
          />
          <span :class="charCount > max_message_length ? 'count-over' : 'count'">{{ charCount }}/{{ max_message_length }}</span>
        </div>

        <button
          type="button"
          style="width: 100%"
          :class="buttonAddClass"
          :disabled="isSubmitDisabled"
          @click="addToCart"
        >Add to cart</button>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { isEmpty } from "lodash-es";

export default {
  name: "PersonalisationInline",
  props: {
    product: {
      type: Object,
      required: true
    },
    variantId: {
      type: String,
      default: ""
    },
    b2bCart: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      message: "",
      adding: false,
      b2bDropdownActive: false,
      b2bDropdown: {
        selected: ""
      },
      personalisationSyncId: `pi-${Math.random().toString(36).slice(2)}`,
      isSyncingMessage: false
    };
  },
  computed: {
    ...mapGetters([
      "cartInstance",
      "isB2B",
      "b2bCompany",
      "feature"
    ]),
    max_message_length() {
      const limit = parseInt(this.product?.shopify_metafields?.personalisation_limit, 10);
      if (Number.isFinite(limit) && limit > 0) {
        return limit;
      }
      return 20;
    },
    charCount() {
      return this.message.length;
    },
    isSubmitDisabled() {
      return this.adding || this.charCount === 0 || this.charCount > this.max_message_length;
    },
    buttonAddClass() {
      return this.isSubmitDisabled ? "preview-button--disabled" : "preview-button";
    },
    activeVariantId() {
      if (this.variantId) {
        return this.variantId;
      }
      const variants = this.product?.product_variants || [];
      return variants.length ? variants[0].id : "";
    },
    productIdValue() {
      return this.product?.shopify_id || "";
    },
    b2bMultiPersonalisation() {
      return !!this.feature?.B2B?.multi_personalisation;
    }
  },
  watch: {
    message(newValue) {
      if (this.isSyncingMessage) {
        this.isSyncingMessage = false;
        return;
      }
      this.broadcastPersonalisationMessage(newValue);
    },
    b2bCart: {
      immediate: true,
      deep: true,
      handler(cart) {
        if (!isEmpty(cart) && cart.personalisationLabel) {
          this.message = cart.personalisationLabel;
        }
      }
    },
    product: {
      immediate: true,
      handler() {
        this.trimMessageToLimit();
      }
    }
  },
  mounted() {
    if (typeof window !== "undefined") {
      window.addEventListener("personalisation-inline-message", this.handleSyncedMessage);
    }
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("personalisation-inline-message", this.handleSyncedMessage);
    }
  },
  methods: {
    broadcastPersonalisationMessage(message) {
      if (typeof window === "undefined") {
        return;
      }
      window.dispatchEvent(new CustomEvent("personalisation-inline-message", {
        detail: {
          sourceId: this.personalisationSyncId,
          message
        }
      }));
    },
    handleSyncedMessage(event) {
      const detail = event?.detail;
      if (!detail || detail.sourceId === this.personalisationSyncId) {
        return;
      }
      this.isSyncingMessage = true;
      this.message = detail.message || "";
    },
    trimMessageToLimit() {
      if (this.message.length > this.max_message_length) {
        this.message = this.message.slice(0, this.max_message_length);
      }
    },
    dropdownCss() {
      const icon = this.b2bDropdownActive ? "zmdi-chevron-up" : "zmdi-chevron-down";
      const state = this.b2bDropdownActive ? "active" : "inactive";
      return `variant-dropdown variant-dropdown--${state} zmdi ${icon}`;
    },
    toggleB2BDropdown() {
      this.b2bDropdownActive = !this.b2bDropdownActive;
    },
    setB2BDropdown(option) {
      this.b2bDropdown.selected = option;
      switch (option) {
        case "name":
          this.message = this.b2bCart?.recipientName || "";
          break;
        case "company":
          this.message = this.b2bCompany?.companyName || "";
          break;
        default:
          this.message = "";
      }
    },
    addToCart() {
      if (this.isSubmitDisabled) {
        return;
      }
      const variantId = this.activeVariantId;
      const productId = this.productIdValue;
      if (!variantId || !productId) {
        return;
      }
      this.adding = true;
      if (!isEmpty(this.b2bCart)) {
        this.cartInstance.updateB2BCartWithPersonalisation(
          this.b2bCart.lineItemId,
          this.b2bCart.lineItemAttributes,
          this.message,
          (responseData) => {
            this.handleCartResponse(responseData, true);
          }
        );
        return;
      }
      this.cartInstance.addToCartWithPersonalisation(
        variantId,
        this.message,
        productId,
        (responseData) => {
          this.handleCartResponse(responseData, false);
        }
      );
    },
    handleCartResponse(responseData, isB2BUpdate) {
      this.adding = false;
      if (!isB2BUpdate && responseData?.status === 200) {
        this.message = "";
        this.b2bDropdown.selected = "";
      }
      window.dispatchEvent(new CustomEvent("cart-changed", {
        detail: {
          cart: this.cartInstance.getCart()
        }
      }));
      if (isB2BUpdate && typeof this.b2bCart?.callback === "function") {
        this.b2bCart.callback(this.message);
      }
    }
  }
};
</script>
